<template>
  <!-- Main Container with modern gradient background -->
  <div class="min-h-screen bg-white dark:bg-gradient-to-br dark:from-[#0c1427] dark:to-[#1a2332]">
    <!-- Stats Cards Section with glass morphism effect -->
    <div class="px-4 sm:px-6 lg:px-8 pt-6 pb-8">
      <!-- Skeleton loader for charts -->
      <div v-if="!showCharts" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div v-for="i in 4" :key="i" class="bg-white dark:bg-white/10 backdrop-blur-[10px] shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl p-6 border border-gray-100 dark:border-white/10">
          <div class="animate-pulse">
            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
            <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
            <div class="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Actual charts -->
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <RevenueGrowthChart
          :current-user-id="currentUserId"
          :user-permissions="userPermissions"
          :statistics-data="statisticsData?.revenueGrowth"
          :is-loading="chartsLoading"
        />

        <LeadConversionChart
          :current-user-id="currentUserId"
          :user-permissions="userPermissions"
          :statistics-data="statisticsData"
          :is-loading="chartsLoading"
        />

        <AnnualProfitChart
          :current-user-id="currentUserId"
          :user-permissions="userPermissions"
          :statistics-data="statisticsData?.annualProfit"
          :is-loading="chartsLoading"
        />
      </div>
    </div>

    <!-- Main Content Card with modern design -->
    <div class="px-4 sm:px-6 lg:px-8 pb-8">
      <div class="bg-white dark:bg-white/10 backdrop-blur-[20px] shadow-[0_20px_40px_0_rgba(0,0,0,0.1)] rounded-3xl overflow-hidden border border-gray-100 dark:border-white/10">

        <!-- Header Section -->
        <div class="px-6 lg:px-8 py-6 border-b border-black/[0.06] dark:border-white/[0.06]">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <!-- Search Bar with modern design -->
            <div class="flex items-center gap-3 w-full sm:w-auto">
              <div class="relative w-full sm:w-auto">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i class="material-symbols-outlined !text-[20px] text-[#8e8e93]">search</i>
                </div>
                <input
                  type="text"
                  :placeholder="directT('crm.leads.searchLead')"
                  v-model="searchQuery"
                  @input="handleSearch"
                  class="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 pl-11 pr-4 h-11 text-[15px] rounded-full w-full sm:w-[320px] text-black dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-blue-500/30 transition-all shadow-md hover:shadow-lg focus:shadow-xl"
                />
              </div>

              <!-- Filter Button -->
              <button
                @click="showFilterModal = true"
                class="h-11 px-5 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 flex items-center gap-2 hover:shadow-lg transition-all shadow-md"
                :class="{ 'ring-2 ring-blue-500': hasActiveFilters }"
              >
                <i class="material-symbols-outlined !text-[20px] text-[#8e8e93] dark:text-gray-400">filter_list</i>
                <span class="text-[15px] font-medium text-[#1d1d1f] dark:text-white">{{ directT('crm.leads.filter') }}</span>
                <span v-if="activeFilterCount > 0" class="ml-1 px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded-full">
                  {{ activeFilterCount }}
                </span>
              </button>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3 w-full sm:w-auto">
              <!-- Bulk Assign Button -->
              <button
                v-if="selectedLeads.length > 0"
                type="button"
                @click="showBulkAssignModal"
                style="background: #007AFF; color: white; padding: 12px 20px; font-size: 15px; font-weight: 500;"
                class="rounded-full flex items-center gap-2 hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <i class="material-symbols-outlined !text-[20px]">person_add</i>
                <span>{{ directT('crm.leads.assignTo') || 'Assign To' }} ({{ selectedLeads.length }})</span>
              </button>

              <!-- Add New Lead Button -->
              <button
                type="button"
                @click="stateStoreInstance.activeBtn"
                style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 20px; font-size: 15px; font-weight: 500;"
                class="rounded-full flex items-center gap-2 hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <i class="material-symbols-outlined !text-[20px]">add</i>
                <span>{{ directT('crm.leads.addNewLead') }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <div class="overflow-x-auto">
          <!-- Loading State -->
          <div v-if="loading" class="flex justify-center items-center py-20">
            <div class="relative">
              <div class="w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
              <div class="w-16 h-16 rounded-full border-4 border-t-blue-500 animate-spin absolute top-0"></div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="leads.length === 0" class="flex flex-col items-center justify-center py-20">
            <div class="w-24 h-24 rounded-full bg-[#f2f2f7] dark:bg-white/5 flex items-center justify-center mb-4">
              <i class="material-symbols-outlined !text-[48px] text-[#8e8e93] dark:text-gray-400">inbox</i> 
            </div>
            <p class="text-lg font-medium text-[#1d1d1f] dark:text-white">No leads found</p>
            <p class="text-sm mt-1 text-[#8e8e93] dark:text-gray-400">Start by adding your first lead</p>
          </div>

          <!-- Table -->
          <table v-else class="w-full">
            <thead>
              <tr style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);">
                <th class="px-6 py-4 text-center">
                  <input
                    type="checkbox"
                    v-model="selectAll"
                    @change="toggleSelectAll"
                    class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:checked:bg-blue-600 dark:checked:border-blue-600"
                  />
                </th>
                <th class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white">
                  {{ directT('dashboard.customer') }}
                </th>
                <th class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white">
                  {{ directT('common.phone') }}
                </th>
                <th class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white">
                  {{ directT('common.nextCall') }}
                </th>
                <th class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white">
                  {{ directT('dashboard.crm.source') }}
                </th>
                <th class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white">
                  {{ directT('dashboard.status') }}
                </th>
                <th class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white">
                  {{ directT('todoList.assignedTo') }}
                </th>
                <th class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white">
                  {{ directT('dashboard.crm.action') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-black/[0.06] dark:divide-white/[0.06]">
              <tr v-for="lead in paginatedLeads" :key="lead.id"
                  @click="handleRowClick(lead, $event)"
                  class="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors cursor-pointer">
                <td class="px-6 py-4 text-center">
                  <input
                    type="checkbox"
                    :value="lead.id"
                    v-model="selectedLeads"
                    class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:checked:bg-blue-600 dark:checked:border-blue-600"
                  />
                </td>
                <td class="px-6 py-4 text-center">
                  <div class="flex items-center justify-center">
                    <div class="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white dark:ring-gray-800 shadow-md flex-shrink-0 mr-3">
                      <img :src="getLeadImageUrl(lead)" :alt="getLeadName(lead)" class="w-full h-full object-cover">
                    </div>
                    <div class="min-w-[120px]">
                      <p class="text-sm font-semibold text-[#1d1d1f] dark:text-white truncate">{{ getLeadName(lead) }}</p>
                      <p class="text-xs text-[#8e8e93] dark:text-gray-400 truncate">{{ getCompanyName(lead) }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="text-sm text-[#3c3c43] dark:text-gray-300">{{ lead.phone || '—' }}</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="text-sm font-medium" :class="getNextCallClass(lead.next_call_date)">
                    {{ formatNextCallDate(lead.next_call_date) }}
                  </span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="text-sm font-medium px-3 py-1 rounded-full inline-flex items-center gap-1"
                        :style="{
                          backgroundColor: isDarkMode() ? getSourceColor(lead.source).darkBg : getSourceColor(lead.source).bg,
                          color: isDarkMode() ? getSourceColor(lead.source).darkText : getSourceColor(lead.source).text
                        }">
                    <i class="material-symbols-outlined !text-[16px]">{{ getSourceIcon(lead.source) }}</i>
                    {{ lead.source }}
                  </span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="px-3 py-1 rounded-full text-xs font-semibold inline-block"
                        :style="getStatusClass(lead.status)">
                    {{ lead.status || 'New' }}
                  </span>
                </td>
                <td class="px-6 py-4 text-center">
                  <button v-if="lead.assigned_to"
                          @click="showAssignModal(lead)"
                          class="text-sm font-medium text-[#007AFF] hover:opacity-80 transition-opacity">
                    {{ getAssignedUserName(lead) }}
                  </button>
                  <button v-else
                          @click="showAssignModal(lead)"
                          class="text-sm inline-flex items-center justify-center gap-1 text-[#8e8e93] dark:text-gray-400 hover:opacity-80 transition-opacity">
                    <i class="material-symbols-outlined !text-[18px]">person_add</i>
                    Assign
                  </button>
                </td>
                <td class="px-6 py-4 text-center">
                  <div class="flex items-center justify-center gap-2">
                    <button
                      @click="viewLead(lead)"
                      class="w-8 h-8 rounded-full bg-[#34C759] text-white flex items-center justify-center transition-all hover:scale-110"
                    >
                      <i class="material-symbols-outlined !text-[18px]">edit</i>
                    </button>
                    <button
                      v-if="userPermissions.is_owner"
                      @click="deleteLead(lead)"
                      class="w-8 h-8 rounded-full bg-[#FF3B30] text-white flex items-center justify-center transition-all hover:scale-110"
                    >
                      <i class="material-symbols-outlined !text-[18px]">delete</i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="px-6 lg:px-8 py-4 border-t border-black/[0.06] dark:border-white/[0.06]">
          <Pagination
            :items="itemsPerPage"
            :total="totalFilteredLeads"
            :currentPage="currentPage"
            @page-change="handlePageChange"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50 px-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeDeleteModal"></div>
    <div class="relative transform transition-all duration-300 scale-100 bg-white dark:bg-gray-900/95 backdrop-blur-[20px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl p-8 w-full max-w-md">

      <!-- Icon -->
      <div class="w-16 h-16 rounded-full bg-[#FFE5E5] dark:bg-red-500/20 mx-auto mb-6 flex items-center justify-center">
        <i class="material-symbols-outlined !text-[32px] text-[#FF3B30]">delete</i>
      </div>

      <!-- Content -->
      <h3 class="text-2xl font-semibold text-center mb-3 text-[#1d1d1f] dark:text-white">{{ directT('crm.leads.deleteLeadQuestion') }}</h3>
      <p class="text-center mb-8 text-[#8e8e93] dark:text-gray-400">
        {{ directT('crm.leads.deleteLeadConfirmation') }}
      </p>

      <!-- Actions -->
      <div class="flex gap-3">
        <button
          @click="closeDeleteModal"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#f2f2f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white"
        >
          {{ directT('crm.leads.cancel') }}
        </button>
        <button
          @click="confirmDelete"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#FF3B30] text-white"
        >
          {{ directT('crm.leads.delete') }}
        </button>
      </div>
    </div>
  </div>

  <!-- View Lead Modal -->
  <div v-if="showViewModal" class="fixed inset-0 flex items-center justify-center z-50 px-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeViewModal"></div>
    <div class="relative transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900/95 backdrop-blur-[20px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl p-8 w-full max-w-4xl">

      <!-- Header -->
      <div class="flex justify-between items-start mb-8 pb-6 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-4">
          <div class="w-16 h-16 rounded-2xl overflow-hidden ring-4 ring-white dark:ring-gray-800 shadow-lg">
            <img v-if="leadToView" :src="getLeadImageUrl(leadToView)" :alt="getLeadName(leadToView)" class="w-full h-full object-cover">
          </div>
          <div>
            <h3 class="text-3xl font-bold text-[#1d1d1f] dark:text-white">
              {{ leadToView ? getLeadName(leadToView) : '' }}
            </h3>
            <p class="text-lg text-[#8e8e93] dark:text-gray-400 mt-1">
              {{ leadToView ? getCompanyName(leadToView) : '' }}
            </p>
          </div>
        </div>
        <button
          @click="closeViewModal"
          class="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110 hover:rotate-90"
        >
          <i class="material-symbols-outlined text-[#8e8e93] dark:text-gray-400">close</i>
        </button>
      </div>

      <!-- Content -->
      <div v-if="leadToView" class="space-y-8">
        <!-- Contact Information -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div class="p-4 rounded-2xl bg-[#f9fafb] dark:bg-white/5">
            <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-[#8e8e93] dark:text-gray-400">Email</p>
            <p class="font-medium text-[#1d1d1f] dark:text-white">{{ leadToView.email }}</p>
          </div>
          <div class="p-4 rounded-2xl bg-[#f9fafb] dark:bg-white/5">
            <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-[#8e8e93] dark:text-gray-400">Phone</p>
            <p class="font-medium text-[#1d1d1f] dark:text-white">{{ leadToView.phone || '—' }}</p>
          </div>
          <div class="p-4 rounded-2xl bg-[#f9fafb] dark:bg-white/5">
            <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-[#8e8e93] dark:text-gray-400">Source</p>
            <p class="font-medium text-[#1d1d1f] dark:text-white">{{ leadToView.source }}</p>
          </div>
          <div class="p-4 rounded-2xl bg-[#f9fafb] dark:bg-white/5">
            <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-[#8e8e93] dark:text-gray-400">Status</p>
            <span class="px-3 py-1 rounded-full text-xs font-semibold inline-block"
                  :style="getStatusClass(leadToView.status)">
              {{ leadToView.status || 'New' }}
            </span>
          </div>
          <div class="p-4 rounded-2xl bg-[#f9fafb] dark:bg-white/5">
            <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-[#8e8e93] dark:text-gray-400">Created</p>
            <p class="font-medium text-[#1d1d1f] dark:text-white">{{ formatDate(leadToView.created_at) }}</p>
          </div>
        </div>

        <!-- Notes Section -->
        <div v-if="leadToView.notes" class="p-4 rounded-2xl bg-[#f9fafb] dark:bg-white/5">
          <p class="text-xs font-semibold uppercase tracking-wider mb-2 text-[#8e8e93] dark:text-gray-400">Notes</p>
          <p class="whitespace-pre-line text-[#1d1d1f] dark:text-white">{{ leadToView.notes }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 mt-8">
        <button
          @click="closeViewModal"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#f2f2f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white"
        >
          Close
        </button>
        <button
          @click="editLead(leadToView)"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#007AFF] text-white"
        >
          {{ directT('crm.leads.editLead') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Filter Modal -->
  <div v-if="showFilterModal" class="fixed inset-0 flex items-center justify-center z-50 px-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeFilterModal"></div>
    <div class="relative transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900/95 backdrop-blur-[20px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl p-8 w-full max-w-2xl">

      <!-- Header -->
      <div class="flex justify-between items-start mb-6 pb-6 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#667eea] to-[#764ba2] flex items-center justify-center shadow-lg">
            <i class="material-symbols-outlined text-white !text-[24px]">filter_list</i>
          </div>
          <div>
            <h3 class="text-2xl font-bold bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
              {{ directT('crm.leads.filter') }}
            </h3>
            <p class="text-sm text-[#8e8e93] dark:text-gray-400 mt-1">
              Filter leads by various criteria
            </p>
          </div>
        </div>
        <button
          @click="closeFilterModal"
          class="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110 hover:rotate-90"
        >
          <i class="material-symbols-outlined text-[#8e8e93] dark:text-gray-400">close</i>
        </button>
      </div>

      <!-- Filter Content -->
      <div class="space-y-6">
        <!-- Status Filter -->
        <div>
          <h4 class="text-lg font-semibold mb-3 text-[#1d1d1f] dark:text-white">Status</h4>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="status in statuses"
              :key="status.name"
              @click="toggleFilter('status', status.name)"
              class="px-3 py-1 rounded-full text-sm font-medium transition-all"
              :class="filters.status.includes(status.name) ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20'"
            >
              {{ status.name }}
            </button>
          </div>
        </div>

        <!-- Source Filter -->
        <div>
          <h4 class="text-lg font-semibold mb-3 text-[#1d1d1f] dark:text-white">Source</h4>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="source in sources"
              :key="source.name"
              @click="toggleFilter('source', source.name)"
              class="px-3 py-1 rounded-full text-sm font-medium transition-all"
              :class="filters.source.includes(source.name) ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20'"
            >
              {{ source.name }}
            </button>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 mt-8">
        <button
          @click="clearAllFilters"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#f2f2f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white"
        >
          Clear All
        </button>
        <button
          @click="applyFilters"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#007AFF] text-white"
        >
          Apply Filters
        </button>
      </div>
    </div>
  </div>

  <!-- View Lead Modal -->
  <div v-if="showViewModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="closeViewModal"></div>
    <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl w-full max-w-2xl z-10 relative overflow-hidden border border-[#E5E7EB] dark:border-[#374151] max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="bg-gradient-to-r from-[#007AFF] to-[#5856D6] p-6">
        <div class="flex justify-between items-center">
          <h3 class="text-xl font-bold text-white">Lead Details</h3>
          <button
            @click="closeViewModal" 
            class="p-2 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all duration-200"
          >
            <i class="material-symbols-outlined text-xl">close</i>
          </button>
        </div>
      </div>
      
      <!-- Content -->
      <div v-if="leadToView" class="p-6">
        <!-- Profile Section -->
        <div class="flex flex-col sm:flex-row sm:items-center mb-8 p-6 bg-gradient-to-r from-[#F0F7FF] to-[#F0F4FF] dark:from-[#0c1427] dark:to-[#1a1a2e] rounded-2xl border border-[#007AFF]/20">
          <div class="relative mb-4 sm:mb-0 sm:mr-6">
            <img :src="getLeadImageUrl(leadToView)" class="w-20 h-20 rounded-2xl object-cover ring-4 ring-[#007AFF]/20" :alt="leadToView.name">
            <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-[#34C759] to-[#00D4AA] rounded-xl flex items-center justify-center">
              <i class="material-symbols-outlined text-white !text-[16px]">person</i>
            </div>
          </div>
          <div>
            <h4 class="text-2xl font-bold text-[#1a1a1a] dark:text-white mb-1">{{ leadToView.name }}</h4>
            <p class="text-[#007AFF] dark:text-[#40A9FF] font-medium">ID: {{ getShortId(leadToView.id) }}</p>
          </div>
        </div>
      
        <!-- Details Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div class="p-4 bg-[#F0F7FF] dark:bg-[#0c1427] rounded-2xl border border-[#007AFF]/10">
            <div class="flex items-center mb-2">
              <i class="material-symbols-outlined text-[#007AFF] mr-2 !text-[20px]">email</i>
              <p class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">Email</p>
            </div>
            <p class="text-[#1a1a1a] dark:text-white font-medium break-words">{{ leadToView.email }}</p>
          </div>
          
          <div class="p-4 bg-[#F0F9F5] dark:bg-[#0a1a0f] rounded-2xl border border-[#34C759]/10">
            <div class="flex items-center mb-2">
              <i class="material-symbols-outlined text-[#34C759] mr-2 !text-[20px]">phone</i>
              <p class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">Phone</p>
            </div>
            <p class="text-[#1a1a1a] dark:text-white font-medium">{{ leadToView.phone || 'N/A' }}</p>
          </div>
          
          <div class="p-4 bg-[#FFF7E6] dark:bg-[#1a1406] rounded-2xl border border-[#FF9500]/10">
            <div class="flex items-center mb-2">
              <i class="material-symbols-outlined text-[#FF9500] mr-2 !text-[20px]">business</i>
              <p class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">Company</p>
            </div>
            <p class="text-[#1a1a1a] dark:text-white font-medium">{{ getCompanyName(leadToView) }}</p>
          </div>
          
          <div class="p-4 bg-[#F8F0FF] dark:bg-[#1a0d1a] rounded-2xl border border-[#5856D6]/10">
            <div class="flex items-center mb-2">
              <i class="material-symbols-outlined text-[#5856D6] mr-2 !text-[20px]">source</i>
              <p class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">Source</p>
            </div>
            <p class="text-[#1a1a1a] dark:text-white font-medium">{{ leadToView.source }}</p>
          </div>
          
          <div class="p-4 bg-[#FFF0F5] dark:bg-[#1a0a12] rounded-2xl border border-[#FF2D92]/10">
            <div class="flex items-center mb-2">
              <i class="material-symbols-outlined text-[#FF2D92] mr-2 !text-[20px]">flag</i>
              <p class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">Status</p>
            </div>
            <span
              class="px-3 py-1 rounded-xl font-medium text-sm"
              :style="getStatusClass(leadToView.status)"
            >
              {{ leadToView.status || 'Close' }}
            </span>
          </div>
          
          <div class="p-4 bg-[#F0F7FF] dark:bg-[#0c1427] rounded-2xl border border-[#007AFF]/10">
            <div class="flex items-center mb-2">
              <i class="material-symbols-outlined text-[#007AFF] mr-2 !text-[20px]">calendar_today</i>
              <p class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">Created Date</p>
            </div>
            <p class="text-[#1a1a1a] dark:text-white font-medium">{{ formatDate(leadToView.created_at) }}</p>
          </div>
        </div>
        
        <!-- Notes Section -->
        <div v-if="leadToView.notes" class="p-6 bg-gradient-to-r from-[#F8F9FA] to-[#E9ECEF] dark:from-[#0c0c0c] dark:to-[#1a1a1a] rounded-2xl border border-[#E5E7EB] dark:border-[#374151]">
          <div class="flex items-center mb-3">
            <i class="material-symbols-outlined text-[#666666] dark:text-[#cccccc] mr-2 !text-[20px]">note</i>
            <p class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">Notes</p>
          </div>
          <p class="text-[#1a1a1a] dark:text-white whitespace-pre-line break-words">{{ leadToView.notes }}</p>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="border-t border-[#E5E7EB] dark:border-[#374151] p-6 bg-[#F8F9FA] dark:bg-[#0c0c0c]">
        <div class="flex justify-end gap-3">
          <button
            type="button"
            @click="closeViewModal"
            class="py-3 px-6 bg-[#F0F7FF] dark:bg-[#0c1427] text-[#007AFF] dark:text-[#40A9FF] font-medium rounded-2xl transition-all duration-200 hover:bg-[#E6F3FF] dark:hover:bg-[#1a2642] border border-[#007AFF]/20"
          >
            Close
          </button>
          <button
            type="button"
            @click="editLead(leadToView!)"
            class="py-3 px-6 bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white font-medium rounded-2xl transition-all duration-200 hover:from-[#0056CC] hover:to-[#4338CA] shadow-lg"
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Edit Lead Modal -->
  <div v-if="showEditModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="closeEditModal"></div>
    <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl w-full max-w-4xl z-10 relative overflow-hidden border border-[#E5E7EB] dark:border-[#374151] max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="bg-gradient-to-r from-[#34C759] to-[#00D4AA] p-6">
        <div class="flex justify-between items-center">
          <h3 class="text-xl font-bold text-white">Edit Lead</h3>
          <button
            @click="closeEditModal" 
            class="p-2 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all duration-200"
          >
            <i class="material-symbols-outlined text-xl">close</i>
          </button>
        </div>
      </div>
      
      <!-- Content -->
      <div v-if="leadToEdit" class="p-6">
        <form @submit.prevent="updateLead" class="space-y-6">
          <!-- Basic Information -->
          <div class="p-6 bg-gradient-to-r from-[#F0F7FF] to-[#F0F4FF] dark:from-[#0c1427] dark:to-[#1a1a2e] rounded-2xl border border-[#007AFF]/20">
            <h4 class="text-lg font-bold text-[#1a1a1a] dark:text-white mb-4 flex items-center">
              <i class="material-symbols-outlined text-[#007AFF] mr-2 !text-[24px]">person</i>
              Basic Information
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">Name *</label>
                <input 
                  type="text" 
                  v-model="leadToEdit.name" 
                  required
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all placeholder:text-[#999] focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10"
                  placeholder="Enter lead name"
                />
              </div>
              <div>
                <label class="block text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">Email *</label>
                <input 
                  type="email" 
                  v-model="leadToEdit.email" 
                  required
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all placeholder:text-[#999] focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label class="block text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">Phone</label>
                <input 
                  type="text" 
                  v-model="leadToEdit.phone"
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all placeholder:text-[#999] focus:border-[#34C759] focus:ring-4 focus:ring-[#34C759]/10"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label class="block text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">Status *</label>
                <select 
                  v-model="leadToEdit.status"
                  required
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none cursor-pointer transition-all focus:border-[#FF9500] focus:ring-4 focus:ring-[#FF9500]/10"
                >
                  <option value="" disabled>Select Status</option>
                  <option v-for="status in statuses" :key="status.id" :value="status.name">{{ status.name }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">Source *</label>
                <select 
                  v-model="leadToEdit.source"
                  required
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none cursor-pointer transition-all focus:border-[#5856D6] focus:ring-4 focus:ring-[#5856D6]/10"
                >
                  <option value="" disabled>Select Source</option>
                  <option v-for="source in sources" :key="source.id" :value="source.name">{{ source.name }}</option>
                </select>
              </div>
              <!-- Income field - only shown when status is Close -->
              <div v-if="leadToEdit.status && leadToEdit.status.toLowerCase() === 'close'">
                <label class="block text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">Income</label>
                <div class="relative">
                  <span class="absolute left-4 top-1/2 -translate-y-1/2 text-[#666666] dark:text-[#cccccc] font-medium">$</span>
                  <input 
                    type="number" 
                    v-model="leadToEdit.dealamount"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] pl-8 pr-4 outline-none transition-all placeholder:text-[#999] focus:border-[#34C759] focus:ring-4 focus:ring-[#34C759]/10"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <!-- Custom Fields Section -->
          <div v-if="customFields.filter(f => f.required).length > 0" class="p-6 bg-gradient-to-r from-[#FFF7E6] to-[#FEF3C7] dark:from-[#1a1406] dark:to-[#2a2306] rounded-2xl border border-[#FF9500]/20">
            <h4 class="text-lg font-bold text-[#1a1a1a] dark:text-white mb-4 flex items-center">
              <i class="material-symbols-outlined text-[#FF9500] mr-2 !text-[24px]">tune</i>
              Custom Fields
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <template v-for="field in customFields.filter(f => f.required)" :key="field.key">
                <div class="mb-1" :class="{'md:col-span-2': field.type === 'textarea'}">
                  <label class="block text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">
                    {{ field.label }} {{ field.required ? '*' : '' }}
                  </label>
                  
                  <!-- Text, number, email, phone inputs -->
                  <input 
                    v-if="getInputType(field.type) !== 'select' && field.type !== 'textarea'"
                    :type="getInputType(field.type)"
                    v-model="customFieldValues[field.key]"
                    :required="field.required"
                    class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all placeholder:text-[#999] focus:border-[#FF9500] focus:ring-4 focus:ring-[#FF9500]/10"
                  />
                  
                  <!-- Textarea for text area fields -->
                  <textarea 
                    v-else-if="field.type === 'textarea'"
                    v-model="customFieldValues[field.key]"
                    :required="field.required"
                    rows="3"
                    class="w-full rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 py-3 outline-none transition-all placeholder:text-[#999] focus:border-[#FF9500] focus:ring-4 focus:ring-[#FF9500]/10"
                  ></textarea>
                  
                  <!-- Dropdown for select fields -->
                  <select
                    v-else
                    v-model="customFieldValues[field.key]"
                    :required="field.required"
                    class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none cursor-pointer transition-all focus:border-[#FF9500] focus:ring-4 focus:ring-[#FF9500]/10"
                  >
                    <option value="">Select</option>
                    <option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
                  </select>
                </div>
              </template>
            </div>
          </div>
          
          <!-- Notes Section -->
          <div class="p-6 bg-gradient-to-r from-[#F8F0FF] to-[#F3E8FF] dark:from-[#1a0d1a] dark:to-[#2a1a2a] rounded-2xl border border-[#5856D6]/20">
            <label class="block text-lg font-bold text-[#1a1a1a] dark:text-white mb-4 flex items-center">
              <i class="material-symbols-outlined text-[#5856D6] mr-2 !text-[24px]">note</i>
              Notes
            </label>
            <textarea 
              v-model="leadToEdit.notes"
              rows="4"
              placeholder="Add any additional notes or comments..."
              class="w-full rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 py-3 outline-none transition-all placeholder:text-[#999] focus:border-[#5856D6] focus:ring-4 focus:ring-[#5856D6]/10"
            ></textarea>
          </div>
          
          <!-- Footer Actions -->
          <div class="flex justify-end gap-3 pt-4">
            <button
              type="button"
              @click="closeEditModal"
              class="py-3 px-6 bg-[#F0F7FF] dark:bg-[#0c1427] text-[#007AFF] dark:text-[#40A9FF] font-medium rounded-2xl transition-all duration-200 hover:bg-[#E6F3FF] dark:hover:bg-[#1a2642] border border-[#007AFF]/20"
            >
              Cancel
            </button>
            <button
              type="submit" 
              class="py-3 px-6 bg-gradient-to-r from-[#34C759] to-[#00D4AA] text-white font-medium rounded-2xl transition-all duration-200 hover:from-[#28A745] hover:to-[#00B894] shadow-lg"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  
  <!-- Toast Notification -->
  <div 
    v-if="toastMessage" 
    class="fixed bottom-6 right-6 left-6 md:left-auto md:max-w-md z-50"
  >
    <div class="p-4 rounded-2xl shadow-2xl transform transition-all duration-300 border border-white/20 backdrop-blur-sm"
         :class="{ 
           'bg-gradient-to-r from-[#34C759] to-[#00D4AA] text-white': toastType === 'success', 
           'bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] text-white': toastType === 'error', 
           'bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white': toastType === 'info' 
         }">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <div class="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center mr-3">
            <i class="material-symbols-outlined !text-[20px]" 
               :class="{ 
                 'text-white': toastType === 'success' || toastType === 'error' || toastType === 'info'
               }">
              {{ toastType === 'success' ? 'check_circle' : toastType === 'error' ? 'error' : 'info' }}
            </i>
          </div>
          <span class="font-medium">{{ toastMessage }}</span>
        </div>
        <button @click="closeToast" class="ml-4 p-1 rounded-xl bg-white/20 hover:bg-white/30 transition-all duration-200">
          <i class="material-symbols-outlined !text-[20px] text-white">close</i>
        </button>
      </div>
    </div>
  </div>

  <!-- Assign Modal -->
  <div v-if="showingAssignModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="closeAssignModal"></div>
    <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl w-full max-w-lg z-10 relative overflow-hidden border border-[#E5E7EB] dark:border-[#374151]">
      <!-- Header -->
      <div class="bg-gradient-to-r from-[#FF9500] to-[#FFB020] p-6">
        <div class="flex justify-between items-center">
          <h3 class="text-xl font-bold text-white">
            {{ isBulkAssign ? `Assign ${selectedLeads.length} Selected Leads` : 'Assign Lead' }}
          </h3>
          <button
            @click="closeAssignModal" 
            class="p-2 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all duration-200"
          >
            <i class="material-symbols-outlined text-xl">close</i>
          </button>
        </div>
      </div>
      
      <!-- Content -->
      <div class="p-6">
        <div class="mb-6">
          <p class="text-[#1a1a1a] dark:text-white font-medium mb-1">
            {{ isBulkAssign ? 'Select a user to assign these leads to:' : `Assigning lead: ${leadToAssign?.name || ''}` }}
          </p>
          <p class="text-[#666666] dark:text-[#cccccc] text-sm">
            Choose from your organization's users below
          </p>
        </div>
        
        <div v-if="loadingUsers" class="text-center py-8">
          <div class="inline-block">
            <div class="w-12 h-12 border-4 border-[#FF9500]/30 border-t-[#FF9500] rounded-full animate-spin"></div>
          </div>
        </div>
        
        <div v-else class="max-h-64 overflow-y-auto">
          <div class="space-y-2">
            <div 
              v-for="user in users" 
              :key="user.user_id"
              @click="isBulkAssign ? assignSelectedLeads(user.user_id) : assignLead(user.user_id)"
              class="p-4 rounded-2xl border border-[#E5E7EB] dark:border-[#374151] hover:border-[#FF9500] dark:hover:border-[#FF9500] bg-white dark:bg-[#0c0c0c] hover:bg-[#FFF7E6] dark:hover:bg-[#1a1406] cursor-pointer transition-all duration-200 group"
            >
              <div class="flex items-center">
                <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF9500] to-[#FFB020] flex items-center justify-center text-white font-bold text-lg mr-4 group-hover:scale-105 transition-transform duration-200">
                  {{ user.display_name ? user.display_name.substring(0, 1).toUpperCase() : user.email.substring(0, 1).toUpperCase() }}
                </div>
                <div>
                  <p class="font-semibold text-[#1a1a1a] dark:text-white">{{ user.display_name || user.email }}</p>
                  <p class="text-sm text-[#666666] dark:text-[#cccccc]">{{ user.email }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="border-t border-[#E5E7EB] dark:border-[#374151] p-6 bg-[#F8F9FA] dark:bg-[#0c0c0c]">
        <div class="flex justify-end gap-3">
          <button
            type="button"
            @click="closeAssignModal"
            class="py-3 px-6 bg-[#F0F7FF] dark:bg-[#0c1427] text-[#007AFF] dark:text-[#40A9FF] font-medium rounded-2xl transition-all duration-200 hover:bg-[#E6F3FF] dark:hover:bg-[#1a2642] border border-[#007AFF]/20"
          >
            Cancel
          </button>
          <button
            v-if="currentUserId && !isBulkAssign"
            type="button"
            @click="assignLead(null)"
            class="py-3 px-6 bg-gradient-to-r from-[#666666] to-[#999999] text-white font-medium rounded-2xl transition-all duration-200 hover:from-[#555555] hover:to-[#888888] shadow-lg"
          >
            Unassign
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onActivated, computed, watch, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { supabase } from "@/lib/supabaseClient";
import stateStore from "@/utils/store";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import RevenueGrowthChart from "./RevenueGrowthChart.vue";
import LeadConversionChart from "./LeadConversionChart.vue";

import AnnualProfitChart from "./AnnualProfitChart.vue";
import Pagination from "@/components/Common/Pagination.vue";
import { createToast } from "@/utils/toast";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  source: string;
  notes: string;
  data: any;
  created_by: string;
  assigned_to: string | null;
  org_id: string;
  created_at: string;
  updated_at: string;
  dealamount?: number;
  dealtime?: string;
}

interface Status {
  id: string;
  name: string;
  color: string;
}

interface Source {
  id: string;
  name: string;
}

interface UserPermissions {
  is_owner?: boolean;
  edit_all_leads?: boolean;
  view_all_leads?: boolean;
  can_invite_users?: boolean;
  [key: string]: any;
}

interface CustomField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
}

// Type definition for nested translation objects
type TranslationObject = { [key: string]: string | TranslationObject };

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Leads",
  components: {
    // Charts components used in the dashboard section
    // eslint-disable-next-line vue/no-unused-components
    RevenueGrowthChart,
    // eslint-disable-next-line vue/no-unused-components
    LeadConversionChart, 
    // eslint-disable-next-line vue/no-unused-components

    // eslint-disable-next-line vue/no-unused-components
    AnnualProfitChart,
    // Pagination component
    Pagination,
  },
  setup() {
    const router = useRouter();
    const stateStoreInstance = stateStore;
    const loading = ref(true);
    const organizationId = ref<string | null>(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const selectedLeads = ref<string[]>([]);
    const selectAll = ref(false);
    
    // Add the missing leads ref declaration
    const leads = ref<Lead[]>([]);
    
    // User permissions
    const currentUserId = ref<string | null>(null);
    const userPermissions = ref<UserPermissions>({
      is_owner: false,
      edit_all_leads: false,
      view_all_leads: false,
      can_invite_users: false
    });
    
    // Add a ready state to ensure everything is loaded
    const isReady = ref(false);
    
    // i18n setup
    const { locale } = useI18n();
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    // Delete modal
    const showDeleteModal = ref(false);
    const leadToDelete = ref<Lead | null>(null);
    
    // Edit modal
    const showEditModal = ref(false);
    const leadToEdit = ref<Lead | null>(null);
    
    // View modal
    const showViewModal = ref(false);
    const leadToView = ref<Lead | null>(null);
    
    // Toast notification
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info'>('info');
    const toastTimeout = ref<number | null>(null);
    
    const statuses = ref<Status[]>([]);
    const sources = ref<Source[]>([]);
    
    // Store for organization users
    const users = ref<{ user_id: string; display_name: string; email: string }[]>([]);
    const loadingUsers = ref(false);
    
    // Lead assignment
    const showingAssignModal = ref(false);
    const leadToAssign = ref<Lead | null>(null);
    const isBulkAssign = ref(false);
    
    // Custom fields
    const customFields = ref<CustomField[]>([]);
    const customFieldValues = ref<Record<string, string>>({});
    const loadingCustomFields = ref(false);
    
    // New UI features
    const showFilterModal = ref(false);
    const showCharts = ref(true);
    const chartsLoading = ref(false);
    const statisticsData = ref<any>(null);
    
    // Filter system
    const filters = ref({
      status: [] as string[],
      source: [] as string[],
      assignedTo: [] as string[],
      nextCall: [] as string[]
    });
    
    const filteredLeads = computed(() => {
      if (!searchQuery.value) return leads.value;
      
      const query = searchQuery.value.toLowerCase();
      return leads.value.filter(lead => 
        lead.name.toLowerCase().includes(query) ||
        lead.email.toLowerCase().includes(query) ||
        (lead.phone && lead.phone.toLowerCase().includes(query)) ||
        (lead.status && lead.status.toLowerCase().includes(query)) ||
        (lead.source && lead.source.toLowerCase().includes(query)) ||
        (lead.notes && lead.notes.toLowerCase().includes(query))
      );
    });
    
    const paginatedLeads = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage.value;
      const endIndex = startIndex + itemsPerPage.value;
      return filteredLeads.value.slice(startIndex, endIndex);
    });
    
    // Computed properties for new UI features
    const hasActiveFilters = computed(() => {
      return filters.value.status.length > 0 || 
             filters.value.source.length > 0 || 
             filters.value.assignedTo.length > 0 || 
             filters.value.nextCall.length > 0;
    });
    
    const activeFilterCount = computed(() => {
      return filters.value.status.length + 
             filters.value.source.length + 
             filters.value.assignedTo.length + 
             filters.value.nextCall.length;
    });
    
    const totalFilteredLeads = computed(() => {
      return filteredLeads.value.length;
    });
    
    // Reset pagination when search changes
    watch(searchQuery, () => {
      currentPage.value = 1;
    });
    
    // Fetch leads from the database
    const fetchLeads = async () => {
      try {
        loading.value = true;
        
        // Get current user info
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        if (!userData.user) {
          createToast('User is not authenticated', 'error');
          loading.value = false;
          return;
        }
        
        currentUserId.value = userData.user.id;
        
        // Get user profile with organization ID and permissions
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id, permissions_json')
          .eq('user_id', currentUserId.value)
          .single();
          
        if (profileError) throw profileError;
        
        if (!profileData || !profileData.org_id) {
          createToast('User is not associated with an organization', 'error');
          loading.value = false;
          return;
        }
        
        // Store organization ID with proper type assertion
        organizationId.value = profileData.org_id as string;
        
        // Fetch users first to ensure they're available when displaying leads
        await fetchUsers();
        
        // Store permissions for use throughout the component
        const permissions = profileData?.permissions_json as UserPermissions || {};
        userPermissions.value = {
          is_owner: permissions.is_owner === true,
          edit_all_leads: permissions.edit_all_leads === true,
          view_all_leads: permissions.view_all_leads === true,
          can_invite_users: permissions.can_invite_users === true
        };
        
        // Build query based on permissions
        let query = supabase
          .from('leads')
          .select('*')
          .eq('org_id', organizationId.value);
          
        // If user can't view all leads, only show assigned leads
        if (!userPermissions.value.is_owner && !userPermissions.value.view_all_leads) {
          query = query.eq('assigned_to', currentUserId.value);
        }
        
        // Execute query
        const { data, error } = await query.order('created_at', { ascending: false });
        
        if (error) throw error;
        
        // Fix type conversion by using unknown as intermediate type
        leads.value = (data || []) as unknown as Lead[];
        
        if (leads.value.length > 0) {
          createToast(`Successfully loaded ${leads.value.length} leads from database`, 'success');
        } else {
          if (!userPermissions.value.view_all_leads && !userPermissions.value.is_owner) {
            createToast('No leads assigned to you found', 'info');
          } else {
            createToast('No leads found. Create your first lead by clicking "Add New Lead"', 'info');
          }
        }
        
        // Set ready state to true after everything is loaded
        isReady.value = true;
      } catch (error) {
        console.error('Error fetching leads:', error);
        createToast('Failed to load leads', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Fetch status and source options
    const fetchOptions = async () => {
      try {
        // Check if organization ID is available
        if (!organizationId.value) {
          // Get current user
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;
          
          if (!userData.user) {
            console.warn('Cannot fetch options: User not authenticated');
            return;
          }
          
          const userId = userData.user.id;
          
          // Get organization ID from user profile
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .select('org_id')
            .eq('user_id', userId)
            .single();
            
          if (profileError) {
            console.error('Error fetching user profile:', profileError);
            return;
          }
          
          if (!profileData || !profileData.org_id) {
            console.warn('Cannot fetch options: User profile has no organization ID');
            return;
          }
          
          // Update organization ID with proper type assertion
          organizationId.value = profileData.org_id as string;
        }
        
        if (!organizationId.value) {
          console.warn('Cannot fetch options: Missing organization ID');
          return;
        }
        
        // Fetch statuses
        const { data: statusData, error: statusError } = await supabase
          .from('statusleads')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (statusError) throw statusError;
        
        statuses.value = (statusData || []).map((item: any) => ({
          id: item.id,
          name: item.status,
          color: item.color || '#605DFF'
        }));
        
        // Fetch sources
        const { data: sourceData, error: sourceError } = await supabase
          .from('leadsources')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (sourceError) throw sourceError;
        
        sources.value = (sourceData || []).map((item: any) => ({
          id: item.id,
          name: item.source
        }));
        
        // Fetch custom fields
        await fetchCustomFields();
      } catch (error) {
        console.error('Error fetching options:', error);
        createToast('Failed to fetch options', 'error');
      }
    };
    
    // Get appropriate input type based on custom field type
    const getInputType = (fieldType: string) => {
      switch (fieldType) {
        case 'number': return 'number';
        case 'email': return 'email';
        case 'phone': return 'tel';
        case 'date': return 'date';
        case 'checkbox': return 'checkbox';
        case 'select': return 'select';
        default: return 'text';
      }
    };
    
    // Fetch custom fields
    const fetchCustomFields = async () => {
      try {
        if (!organizationId.value) {
          console.warn('Cannot fetch custom fields: Missing organization ID');
          return;
        }
        
        loadingCustomFields.value = true;
        
        // Get custom fields definition
        const { data, error } = await supabase
          .from('customleadsfield')
          .select('id, content')
          .eq('organizationid', organizationId.value);
        
        if (error) throw error;
        
        if (!data || data.length === 0) {
          // No custom fields defined
          customFields.value = [];
          return;
        }
        
        // Reset the custom fields array
        customFields.value = [];
        
        // Process all records
        for (const record of data) {
          if (record && record.content) {
            // Add type assertion to content
            const content = record.content as any[];
            
            if (Array.isArray(content) && content.length > 0) {
              // Process each field in the content array
              for (const field of content) {
                if (field) {
                  (field as any).customFieldsId = record.id;
                  customFields.value.push(field as CustomField);
                  
                  // Initialize field value
                  customFieldValues.value[field.key] = '';
                }
              }
            }
          }
        }
        
        console.log('Custom fields loaded:', customFields.value);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        createToast('Failed to load custom fields', 'error');
      } finally {
        loadingCustomFields.value = false;
      }
    };
    
    // Fetch organization users
    const fetchUsers = async () => {
      try {
        loadingUsers.value = true;
        
        // Check if organization ID is available
        if (!organizationId.value) {
          console.warn('Cannot fetch users: Missing organization ID');
          loadingUsers.value = false;
          return;
        }
        
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, display_name, email')
          .eq('org_id', organizationId.value);
          
        if (error) throw error;
        
        users.value = (data || []) as { user_id: string; display_name: string; email: string }[];
      } catch (error) {
        console.error('Error fetching users:', error);
        createToast('Failed to load users', 'error');
      } finally {
        loadingUsers.value = false;
      }
    };
    
    // Format date
    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    };
    
    // Get shortened ID for display
    const getShortId = (id: string) => {
      return `#${id.substring(0, 4)}`;
    };
    
    // Get lead image URL
    const getLeadImageUrl = (lead: Lead) => {
      // Check if lead has a profile image in data.image
      if (lead.data && lead.data.image) {
        return lead.data.image;
      }
      
      // Generate a placeholder image based on lead ID
      const imageIndex = parseInt(lead.id.substring(0, 2), 16) % 15 + 1;
      return require(`@/assets/images/users/user${imageIndex}.jpg`);
    };
    
    // Get company name from lead data
    const getCompanyName = (lead: Lead) => {
      if (lead.data && lead.data.company) {
        return lead.data.company;
      }
      return "N/A";
    };
    
    // Get assigned user name (first name only)
    const getAssignedUserName = (lead: Lead) => {
      if (!lead.assigned_to) return 'Unassigned';
      
      // Look up the user in users array
      const assignedUser = users.value.find(user => user.user_id === lead.assigned_to);
      if (assignedUser) {
        const fullName = assignedUser.display_name || assignedUser.email;
        // Extract first name only
        const firstName = fullName.split(' ')[0];
        return firstName;
      }
      return 'Unknown User';
    };
    
    // Show the assign modal
    const showAssignModal = (lead: Lead) => {
      leadToAssign.value = lead;
      showingAssignModal.value = true;
      isBulkAssign.value = false;
      
      // Ensure users are loaded
      if (users.value.length === 0) {
        fetchUsers();
      }
    };
    
    // Show bulk assign modal for multiple leads
    const showBulkAssignModal = () => {
      if (selectedLeads.value.length === 0) return;
      
      leadToAssign.value = null;
      showingAssignModal.value = true;
      isBulkAssign.value = true;
      
      // Ensure users are loaded
      if (users.value.length === 0) {
        fetchUsers();
      }
    };
    
    // Close the assign modal
    const closeAssignModal = () => {
      showingAssignModal.value = false;
      leadToAssign.value = null;
      isBulkAssign.value = false;
    };
    
    // Assign multiple leads to a user
    const assignSelectedLeads = async (userId: string) => {
      if (selectedLeads.value.length === 0) return;
      
      try {
        loading.value = true;
        
        // תאריך העדכון יהיה זהה לכל השיוכים
        const updateTimestamp = new Date().toISOString();
        
        // עדכון כל אחד מהרשומות שנבחרו
        const { error } = await supabase
          .from('leads')
          .update({
            assigned_to: userId,
            updated_at: updateTimestamp
          })
          .in('id', selectedLeads.value);
          
        if (error) throw error;
        
        // עדכון המידע בתצוגה המקומית
        leads.value.forEach(lead => {
          if (selectedLeads.value.includes(lead.id)) {
            lead.assigned_to = userId;
            lead.updated_at = updateTimestamp;
          }
        });
        
        createToast(`${selectedLeads.value.length} leads assigned successfully`, 'success');
        closeAssignModal();
      } catch (error) {
        console.error('Error assigning leads:', error);
        createToast('Failed to assign leads', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Assign lead to a user
    const assignLead = async (userId: string | null) => {
      if (!leadToAssign.value) return;
      
      try {
        loading.value = true;
        
        const { error } = await supabase
          .from('leads')
          .update({
            assigned_to: userId,
            updated_at: new Date().toISOString()
          })
          .eq('id', leadToAssign.value.id);
          
        if (error) throw error;
        
        // Update the lead in the local array
        const leadIndex = leads.value.findIndex(l => l.id === leadToAssign.value?.id);
        if (leadIndex >= 0 && leadToAssign.value) {
          leads.value[leadIndex].assigned_to = userId;
          leads.value[leadIndex].updated_at = new Date().toISOString();
        }
        
        createToast('Lead assigned successfully', 'success');
        closeAssignModal();
      } catch (error) {
        console.error('Error assigning lead:', error);
        createToast('Failed to assign lead', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Get status class for styling
    const getStatusClass = (status: string) => {
      if (!status) {
        // Default styling for no status
        return {
          backgroundColor: 'transparent',
          color: '#64748b',
          border: '1px solid #e2e8f0'
        };
      }
      
      // First, check if we have this status in our statuses array
      const statusObj = statuses.value.find(s => s.name.toLowerCase() === status.toLowerCase());
      
      if (statusObj && statusObj.color) {
        // Use the status's color from the database
        const colorHex = statusObj.color;
        
        // Convert hex to RGB for generating lighter background colors
        const r = parseInt(colorHex.slice(1, 3), 16);
        const g = parseInt(colorHex.slice(3, 5), 16);
        const b = parseInt(colorHex.slice(5, 7), 16);
        
        // Create minimal styling with subtle border
        return {
          backgroundColor: 'transparent',
          color: colorHex,
          border: `1px solid ${colorHex}30`
        };
      }
      
      // Fallback to minimal hardcoded styles if status not found in database
      switch (status.toLowerCase()) {
        case 'new':
        case 'confirmed':
          return {
            backgroundColor: 'transparent',
            color: '#6366f1',
            border: '1px solid #6366f130'
          };
        case 'in progress':
        case 'qualified': 
        case 'close':
          return {
            backgroundColor: 'transparent',
            color: '#10b981',
            border: '1px solid #10b98130'
          };
        case 'pending':
          return {
            backgroundColor: 'transparent',
            color: '#f59e0b',
            border: '1px solid #f59e0b30'
          };
        case 'rejected':
        case 'closed lost':
          return {
            backgroundColor: 'transparent',
            color: '#ef4444',
            border: '1px solid #ef444430'
          };
        case 'closed won':
          return {
            backgroundColor: 'transparent',
            color: '#3b82f6',
            border: '1px solid #3b82f630'
          };
        default:
          return {
            backgroundColor: 'transparent',
            color: '#64748b',
            border: '1px solid #e2e8f0'
          };
      }
    };
    
    // Toggle select all
    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedLeads.value = paginatedLeads.value.map(lead => lead.id);
      } else {
        selectedLeads.value = [];
      }
    };
    
    // Handle pagination
    const handlePageChange = (page: number) => {
      currentPage.value = page;
    };
    
    // Handle search
    const handleSearch = () => {
      currentPage.value = 1;
    };
    
    // Handle row click - navigate to lead detail page
    const handleRowClick = (lead: Lead, event: Event) => {
      // Check if the click was on a checkbox, button, or input element
      const target = event.target as HTMLElement;
      
      // Don't navigate if clicking on interactive elements
      if (target.tagName === 'INPUT' || 
          target.tagName === 'BUTTON' || 
          target.closest('button') || 
          target.closest('input') ||
          target.classList.contains('no-row-click')) {
        return;
      }
      
      // Navigate to lead detail page
      viewLead(lead);
    };

    // View lead details
    const viewLead = (lead: Lead) => {
      // Navigate to lead detail page instead of showing modal
      router.push(`/crm/leads/${lead.id}`);
    };
    
    // Edit lead
    const editLead = (lead: Lead) => {
      // Create a deep copy to avoid mutation issues
      leadToEdit.value = JSON.parse(JSON.stringify(lead));
      
      // Initialize dealamount field if it doesn't exist
      if (leadToEdit.value && !leadToEdit.value.dealamount) {
        leadToEdit.value.dealamount = 0;
      }
      
      // Ensure options and custom fields are loaded
      if (statuses.value.length === 0 || sources.value.length === 0) {
        fetchOptions();
      }
      
      // Fetch custom field values for this lead
      fetchCustomFieldValues(lead.id);
      
      showEditModal.value = true;
    };
    
    // Fetch custom field values for a specific lead
    const fetchCustomFieldValues = async (leadId: string) => {
      try {
        if (!leadId || customFields.value.length === 0) return;
        
        // Clear previous values
        for (const field of customFields.value) {
          customFieldValues.value[field.key] = '';
        }
        
        // Get all custom field values for this lead
        const { data, error } = await supabase
          .from('customleadsfieldcontent')
          .select('*')
          .eq('leadid', leadId);
        
        if (error) throw error;
        
        // Set values based on database records
        if (data && data.length > 0) {
          for (const record of data) {
            // Find matching field
            const matchingField = customFields.value.find(
              field => (field as any).customFieldsId === record.customleadsfieldid &&
                      (field.key === record.fieldkey || (!record.fieldkey && record.customleadsfieldid === field.key))
            );
            
            if (matchingField) {
              customFieldValues.value[matchingField.key] = (record.content as string) || '';
            }
          }
        }
      } catch (error) {
        console.error('Error fetching custom field values:', error);
      }
    };
    
    // Save custom field values for a lead
    const saveCustomFieldValues = async (leadId: string) => {
      try {
        if (!leadId || customFields.value.length === 0) return;
        
        // Get existing custom field values for this lead
        const { data: existingValues, error: queryError } = await supabase
          .from('customleadsfieldcontent')
          .select('*')
          .eq('leadid', leadId);
          
        if (queryError) {
          console.error('Error fetching existing custom field values:', queryError);
          return;
        }
        
        // Process each custom field
        for (const field of customFields.value) {
          if (!field.required) continue; // Skip fields that aren't required (since we're only showing required fields)
          
          const value = customFieldValues.value[field.key];
          const customFieldsId = (field as any).customFieldsId;
          
          if (!customFieldsId) {
            console.error('Missing customFieldsId for field:', field.key);
            continue;
          }
          
          // Look for existing record for this field
          const existingRecord = existingValues?.find(
            record => record.customleadsfieldid === customFieldsId && 
                    (record.fieldkey === field.key || (!record.fieldkey && record.customleadsfieldid === field.key))
          );
          
          if (existingRecord) {
            // Update existing record
            const { error: updateError } = await supabase
              .from('customleadsfieldcontent')
              .update({ 
                content: value, 
                updated_at: new Date().toISOString() 
              })
              .eq('id', (existingRecord as any).id);
              
            if (updateError) {
              console.error('Error updating custom field:', updateError);
            }
          } else if (value !== undefined && value !== null && value !== '') {
            // Insert new record
            const { error: insertError } = await supabase
              .from('customleadsfieldcontent')
              .insert({
                leadid: leadId,
                customleadsfieldid: customFieldsId,
                fieldkey: field.key,
                content: value,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
              
            if (insertError) {
              console.error('Error inserting custom field:', insertError);
            }
          }
        }
      } catch (error) {
        console.error('Error saving custom field values:', error);
      }
    };
    
    // Handle payment for lead
    const handlePayment = (lead: Lead) => {
      // For now, just show a toast notification
      createToast(`Processing payment for: ${lead.name}`, 'info');
      // In the future, this could open a payment modal or navigate to a payment page
    };
    
    // Delete lead
    const deleteLead = (lead: Lead) => {
      leadToDelete.value = lead;
      showDeleteModal.value = true;
    };
    
    // Close delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      leadToDelete.value = null;
    };
    
    // Close edit modal
    const closeEditModal = () => {
      showEditModal.value = false;
      leadToEdit.value = null;
    };
    
    // Close view modal
    const closeViewModal = () => {
      showViewModal.value = false;
      leadToView.value = null;
    };
    
    // Confirm delete
    const confirmDelete = async () => {
      if (!leadToDelete.value) return;
      
      try {
        const { error } = await supabase
          .from('leads')
          .delete()
          .eq('id', leadToDelete.value.id);
        
        if (error) throw error;
        
        // Remove from local list
        leads.value = leads.value.filter(lead => lead.id !== leadToDelete.value?.id);
        
        // Update selected leads
        selectedLeads.value = selectedLeads.value.filter(id => id !== leadToDelete.value?.id);
        
        createToast('Lead deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting lead:', error);
        createToast('Failed to delete lead', 'error');
      } finally {
        closeDeleteModal();
      }
    };
    
    // Update lead
    const updateLead = async () => {
      if (!leadToEdit.value) return;
      
      try {
        // Handle special case for Close status if it doesn't exist in database
        if (leadToEdit.value.status === 'Close') {
          // Check if Close status exists in statusleads table
          const { data: closeStatusData, error: closeStatusError } = await supabase
            .from('statusleads')
            .select('*')
            .eq('organization_id', organizationId.value!)
            .eq('status', 'Close')
            .maybeSingle();
          
          if (closeStatusError) throw closeStatusError;
          
          // If Close status doesn't exist, create it
          if (!closeStatusData && organizationId.value) {
            await supabase
              .from('statusleads')
              .insert({
                status: 'Close',
                color: '#10B981', // Green color for close status
                organization_id: organizationId.value
              });
          }
          
          // Set dealtime to now for Closed leads (for charts and analytics)
          leadToEdit.value.dealtime = new Date().toISOString();
        }
        
        // Update timestamp
        leadToEdit.value.updated_at = new Date().toISOString();
        
        const { error } = await supabase
          .from('leads')
          .update({
            name: leadToEdit.value.name,
            email: leadToEdit.value.email,
            phone: leadToEdit.value.phone,
            status: leadToEdit.value.status,
            source: leadToEdit.value.source,
            notes: leadToEdit.value.notes,
            data: leadToEdit.value.data,
            updated_at: leadToEdit.value.updated_at,
            dealamount: leadToEdit.value.dealamount,
            dealtime: leadToEdit.value.dealtime
          })
          .eq('id', leadToEdit.value.id);
        
        if (error) throw error;
        
        // Update in local list
        const index = leads.value.findIndex(lead => lead.id === leadToEdit.value?.id);
        if (index !== -1) {
          leads.value[index] = { ...leadToEdit.value };
        }
        
        // Save custom field values
        await saveCustomFieldValues(leadToEdit.value.id);
        
        createToast('Lead updated successfully', 'success');
        closeEditModal();
      } catch (error) {
        console.error('Error updating lead:', error);
        createToast('Failed to update lead', 'error');
      }
    };
    
    // Toast functions
    const createToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      toastMessage.value = message;
      toastType.value = type;
      
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
      }
      
      toastTimeout.value = window.setTimeout(() => {
        closeToast();
      }, 3000);
    };
    
    const closeToast = () => {
      toastMessage.value = '';
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
        toastTimeout.value = null;
      }
    };
    
    // New UI helper functions
    const getLeadName = (lead: Lead) => {
      return lead.name || 'Unknown';
    };
    
    const formatNextCallDate = (dateString: string | null) => {
      if (!dateString) return '—';
      
      const date = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);
      
      // Check if it's today
      if (dateOnly.getTime() === today.getTime()) {
        return `Today ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      }
      
      // Check if it's tomorrow
      if (dateOnly.getTime() === tomorrow.getTime()) {
        return `Tomorrow ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;      
      }
      
      // Check if it's in the past
      if (dateOnly < today) {
        const daysAgo = Math.floor((today.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
        if (daysAgo === 1) {
          return 'Yesterday';
        }
        return `${daysAgo} days ago`;
      }
      
      // Future date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    const getNextCallClass = (dateString: string | null) => {
      if (!dateString) return 'text-[#8e8e93] dark:text-gray-400';
      
      const date = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);
      
      // Past due
      if (dateOnly < today) {
        return 'text-[#FF3B30] font-semibold';
      }
      
      // Today
      if (dateOnly.getTime() === today.getTime()) {
        return 'text-[#FF9500] font-semibold';
      }
      
      // Future
      return 'text-[#34C759]';
    };
    
    const getSourceIcon = (source: string) => {
      const iconMap: Record<string, string> = {
        'Facebook': 'thumb_up',
        'Google': 'search',
        'Referred by Client': 'people',
        'Private Lead': 'person',
        'WhatsApp': 'chat',
        'Taboola': 'campaign',
        'Instagram': 'photo_camera',
        'TikTok': 'music_note',
        'Other': 'more_horiz'
      };
      return iconMap[source] || 'more_horiz';
    };
    
    const getSourceColor = (source: string) => {
      const colorMap: Record<string, { bg: string; text: string; darkBg: string; darkText: string }> = {    
        'Facebook': {
          bg: '#E7F3FF',
          text: '#1877F2',
          darkBg: 'rgba(24, 119, 242, 0.2)',
          darkText: '#4B9BFF'
        },
        'Google': { 
          bg: '#FEF7E0',
          text: '#EA4335',
          darkBg: 'rgba(234, 67, 53, 0.2)',
          darkText: '#FF6B5E'
        },
        'Referred by Client': {
          bg: '#E8F5E9',
          text: '#2E7D32',
          darkBg: 'rgba(46, 125, 50, 0.2)',
          darkText: '#66BB6A'
        },
        'Private Lead': {
          bg: '#F3E5F5',
          text: '#7B1FA2',
          darkBg: 'rgba(123, 31, 162, 0.2)',
          darkText: '#BA68C8'
        },
        'WhatsApp': {
          bg: '#E8F8F5',
          text: '#25D366',
          darkBg: 'rgba(37, 211, 102, 0.2)',
          darkText: '#4FDB80'
        },
        'Taboola': {
          bg: '#FFF3E0',
          text: '#FF6F00',
          darkBg: 'rgba(255, 111, 0, 0.2)',
          darkText: '#FFB74D'
        },
        'Instagram': {
          bg: '#FCE4EC',
          text: '#E91E63',
          darkBg: 'rgba(233, 30, 99, 0.2)',
          darkText: '#F06292'
        },
        'TikTok': {
          bg: '#E0F2F1',
          text: '#00897B',
          darkBg: 'rgba(0, 137, 123, 0.2)',
          darkText: '#4DB6AC'
        },
        'Other': {
          bg: '#F5F5F5',
          text: '#616161',
          darkBg: 'rgba(97, 97, 97, 0.2)',
          darkText: '#9E9E9E'
        }
      };
      return colorMap[source] || colorMap['Other'];
    };
    
    const isDarkMode = () => {
      return document.documentElement.classList.contains('dark');
    };
    
    // Filter functions
    const toggleFilter = (filterType: keyof typeof filters.value, value: string) => {
      const filterArray = filters.value[filterType];
      const index = filterArray.indexOf(value);
      
      if (index > -1) {
        filterArray.splice(index, 1);
      } else {
        filterArray.push(value);
      }
    };
    
    const clearAllFilters = () => {
      filters.value = {
        status: [],
        source: [],
        assignedTo: [],
        nextCall: []
      };
    };
    
    const applyFilters = () => {
      showFilterModal.value = false;
      currentPage.value = 1; // Reset to first page when applying filters
      fetchLeads(); // Re-fetch leads with filters
    };
    
    const closeFilterModal = () => {
      showFilterModal.value = false;
    };
    
    // Refresh options when component becomes active (when returning from settings page)
    const refreshOptionsIfNeeded = async () => {
      if (organizationId.value) {
        console.log('Refreshing leads page options...');
        await fetchOptions();
        console.log('Updated statuses in leads page:', statuses.value.map(s => s.name));
      }
    };

    // Setup event listeners
    onMounted(() => {
      // Listen for refresh events from the AddNewLeadPopup component
      window.addEventListener('refreshLeads', fetchLeads);
      
      // Initial fetch
      fetchLeads();
      fetchOptions();
      
      // Cleanup event listeners
      onBeforeUnmount(() => {
        window.removeEventListener('refreshLeads', fetchLeads);
        if (toastTimeout.value) {
          clearTimeout(toastTimeout.value);
        }
      });
    });

    // Refresh data when component becomes active (e.g., when returning from settings)
    onActivated(async () => {
      await refreshOptionsIfNeeded();
    });

    return {
      router,
      stateStoreInstance,
      leads,
      loading,
      isReady,
      searchQuery,
      currentPage,
      itemsPerPage,
      selectedLeads,
      selectAll,
      filteredLeads,
      paginatedLeads,
      showDeleteModal,
      toastMessage,
      toastType,
      showEditModal,
      showViewModal,
      showFilterModal,
      showCharts,
      chartsLoading,
      statisticsData,
      leadToDelete,
      leadToEdit,
      leadToView,
      leadToAssign,
      showingAssignModal,
      loadingUsers,
      users,
      statuses,
      sources,
      currentUserId,
      userPermissions,
      customFields,
      customFieldValues,
      filters,
      hasActiveFilters,
      activeFilterCount,
      totalFilteredLeads,
      getInputType,
      editLead,
      handlePayment,
      deleteLead,
      confirmDelete,
      updateLead,
      closeEditModal,
      closeDeleteModal,
      closeViewModal,
      closeFilterModal,
      closeToast,
      formatDate,
      toggleSelectAll,
      getShortId,
      getLeadImageUrl,
      getCompanyName,
      getAssignedUserName,
      getLeadName,
      showAssignModal,
      showBulkAssignModal,
      closeAssignModal,
      assignLead,
      assignSelectedLeads,
      isBulkAssign,
      getStatusClass,
      handlePageChange,
      handleRowClick,
      viewLead,
      handleSearch,
      directT,
      loadingCustomFields,
      formatNextCallDate,
      getNextCallClass,
      getSourceIcon,
      getSourceColor,
      isDarkMode,
      toggleFilter,
      clearAllFilters,
      applyFilters,
    };
  },
});
</script>
